import React, { useState, useEffect } from "react";
import confetti from "canvas-confetti";
import SEO from "../components/SEO";
import { AlertTriangle, ChevronUp, ChevronDown, Upload, Award, FileText, Send, CheckCircle, AlertCircle } from "lucide-react";

interface FormState {
  fullName: string;
  email: string;
  phone: string;
  currentLocation?: string;
  institutionName?: string;
  courseOfStudy?: string;
  levelYear?: string;
  matricNumber?: string;
  proposedDuration?: string;
  startDate?: string;
  endDate?: string;
  desiredDepartment?: string;
  coreSkills?: string;
  // NYSC specific
  currentStateOfDeployment?: string;
  stateCode?: string;
  callUpNumber?: string;
  batchStream?: string;
  popMonthYear?: string;
  higherInstitution?: string;
  qualification?: string;
  roleAppliedFor?: string;
  portfolioUrl?: string;
}

const ProgrammesScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"siwes" | "nysc">("siwes");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // SIWES state
  const [siwesForm, setSiwesForm] = useState<FormState>({
    fullName: "",
    email: "",
    phone: "",
    currentLocation: "",
    institutionName: "",
    courseOfStudy: "",
    levelYear: "300L",
    matricNumber: "",
    proposedDuration: "6 Months",
    startDate: "",
    endDate: "",
    desiredDepartment: "Backend Development",
    coreSkills: "",
  });
  const [siwesLetter, setSiwesLetter] = useState<string>("");
  const [siwesIdCard, setSiwesIdCard] = useState<string>("");

  // NYSC state
  const [nyscForm, setNyscForm] = useState<FormState>({
    fullName: "",
    email: "",
    phone: "",
    currentStateOfDeployment: "",
    stateCode: "",
    callUpNumber: "",
    batchStream: "Batch A Stream 1",
    popMonthYear: "",
    higherInstitution: "",
    qualification: "BSc",
    courseOfStudy: "",
    roleAppliedFor: "Software Engineering",
    coreSkills: "",
    portfolioUrl: "",
  });
  const [nyscLetter, setNyscLetter] = useState<string>("");
  const [nyscResume, setNyscResume] = useState<string>("");

  const [programStatuses, setProgramStatuses] = useState<{ siwes: string; nysc: string }>({
    siwes: "enabled",
    nysc: "enabled"
  });

  useEffect(() => {
    fetch("/api/settings")
      .then((res) => res.json())
      .then((data) => {
        setProgramStatuses({
          siwes: data.program_siwes_status || "enabled",
          nysc: data.program_nysc_status || "enabled"
        });
      })
      .catch((err) => console.warn("Failed to fetch program statuses:", err));
  }, []);

  const getFormData = () => {
    if (activeTab === "siwes") {
      return {
        fullName: siwesForm.fullName,
        email: siwesForm.email,
        phone: siwesForm.phone,
        currentLocation: siwesForm.currentLocation,
        institutionName: siwesForm.institutionName,
        courseOfStudy: siwesForm.courseOfStudy,
        levelYear: siwesForm.levelYear,
        matricNumber: siwesForm.matricNumber,
        proposedDuration: siwesForm.proposedDuration,
        startDate: siwesForm.startDate,
        endDate: siwesForm.endDate,
        desiredDepartment: siwesForm.desiredDepartment,
        coreSkills: siwesForm.coreSkills,
        fileLetter: siwesLetter,
        fileIdCard: siwesIdCard,
      };
    } else {
      return {
        fullName: nyscForm.fullName,
        email: nyscForm.email,
        phone: nyscForm.phone,
        currentStateOfDeployment: nyscForm.currentStateOfDeployment,
        stateCode: nyscForm.stateCode,
        callUpNumber: nyscForm.callUpNumber,
        batchStream: nyscForm.batchStream,
        popMonthYear: nyscForm.popMonthYear,
        higherInstitution: nyscForm.higherInstitution,
        qualification: nyscForm.qualification,
        courseOfStudy: nyscForm.courseOfStudy,
        roleAppliedFor: nyscForm.roleAppliedFor,
        coreSkills: nyscForm.coreSkills,
        portfolioUrl: nyscForm.portfolioUrl,
        fileLetter: nyscLetter,
        fileResume: nyscResume,
      };
    }
  };

  const isFormValid = () => {
    if (activeTab === "siwes") {
      return (
        siwesForm.fullName &&
        siwesForm.email &&
        siwesForm.phone &&
        siwesForm.currentLocation &&
        siwesForm.institutionName &&
        siwesForm.courseOfStudy &&
        siwesForm.levelYear &&
        siwesForm.matricNumber &&
        siwesForm.proposedDuration &&
        siwesForm.startDate &&
        siwesForm.endDate &&
        siwesForm.desiredDepartment &&
        siwesLetter &&
        siwesIdCard
      );
    } else {
      return (
        nyscForm.fullName &&
        nyscForm.email &&
        nyscForm.phone &&
        nyscForm.currentStateOfDeployment &&
        nyscForm.stateCode &&
        nyscForm.callUpNumber &&
        nyscForm.batchStream &&
        nyscForm.popMonthYear &&
        nyscForm.higherInstitution &&
        nyscForm.qualification &&
        nyscForm.courseOfStudy &&
        nyscForm.roleAppliedFor &&
        nyscLetter &&
        nyscResume
      );
    }
  };

  const handleApplyClick = () => {
    setIsFormOpen(!isFormOpen);
  };

  const capitalizeInput = (val: string): string => {
    if (!val) return val;
    const prepositions = ["of", "in", "at", "to", "for", "with", "by", "on", "and", "or", "the", "a", "an", "from"];
    const parts = val.split(/(\s+)/);
    let hasFirstWordBeenCapitalized = false;
    return parts.map((part) => {
      if (!part || /^\s+$/.test(part)) return part;
      const lower = part.toLowerCase();
      if (prepositions.includes(lower) && hasFirstWordBeenCapitalized) {
        return lower;
      }
      hasFirstWordBeenCapitalized = true;
      return part.charAt(0).toUpperCase() + part.slice(1);
    }).join("");
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const fieldsToCapitalize = [
      "fullName",
      "currentLocation",
      "institutionName",
      "courseOfStudy",
      "higherInstitution",
      "currentStateOfDeployment",
      "roleAppliedFor",
      "desiredDepartment"
    ];
    let processedValue = fieldsToCapitalize.includes(name) ? capitalizeInput(value) : value;

    if (name === "phone") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length > 11) {
        return;
      }
      processedValue = numericValue;
    }

    if (activeTab === "siwes") {
      setSiwesForm((prev) => ({ ...prev, [name]: processedValue }));
    } else {
      setNyscForm((prev) => ({ ...prev, [name]: processedValue }));
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: "siwesLetter" | "siwesId" | "nyscLetter" | "nyscResume"
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 4 * 1024 * 1024) {
        setPopup({ type: "error", message: "File size exceeds 4MB limit." });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        if (fileType === "siwesLetter") setSiwesLetter(base64String);
        if (fileType === "siwesId") setSiwesIdCard(base64String);
        if (fileType === "nyscLetter") setNyscLetter(base64String);
        if (fileType === "nyscResume") setNyscResume(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) {
      setPopup({ type: "error", message: "Please fill in all required fields and upload all documents." });
      return;
    }

    setLoading(true);
    const formData = getFormData();

    try {
      const endpoint = activeTab === "siwes" ? "/api/apply/siwes" : "/api/apply/nysc";
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json().catch(() => null);

      if (response.ok) {
        // Trigger confetti animation!
        try {
          confetti({
            particleCount: 150,
            spread: 80,
            origin: { y: 0.6 }
          });
        } catch (confettiErr) {
          console.error("Confetti failed to run:", confettiErr);
        }

        setPopup({ type: "success", message: "Application submitted successfully!" });
        setIsFormOpen(false);
        // Reset forms
        setSiwesForm({
          fullName: "",
          email: "",
          phone: "",
          currentLocation: "",
          institutionName: "",
          courseOfStudy: "",
          levelYear: "300L",
          matricNumber: "",
          proposedDuration: "6 Months",
          startDate: "",
          endDate: "",
          desiredDepartment: "Backend Development",
          coreSkills: "",
        });
        setNyscForm({
          fullName: "",
          email: "",
          phone: "",
          currentStateOfDeployment: "",
          stateCode: "",
          callUpNumber: "",
          batchStream: "Batch A Stream 1",
          popMonthYear: "",
          higherInstitution: "",
          qualification: "BSc",
          courseOfStudy: "",
          roleAppliedFor: "Software Engineering",
          coreSkills: "",
        });
        setSiwesLetter("");
        setSiwesIdCard("");
        setNyscLetter("");
        setNyscResume("");
      } else {
        const errMsg = data?.message || "Submission failed. Please try again.";
        setPopup({ type: "error", message: errMsg });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setPopup({ type: "error", message: "An error occurred. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="relative w-full overflow-hidden flex justify-center px-4 md:px-6 lg:px-8 mt-6 md:mt-12 bg-transparent pb-24">
      {/* Dynamic styles */}
      <style>{`
        .mesh-container {
          position: fixed;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
          background: 
            radial-gradient(circle at 15% 15%, rgba(238, 44, 40, 0.08) 0%, transparent 48%),
            radial-gradient(circle at 85% 85%, rgba(12, 27, 46, 0.04) 0%, transparent 45%);
          transition: background 500ms ease;
        }
        .dark .mesh-container {
          background: 
            radial-gradient(circle at 15% 15%, rgba(238, 44, 40, 0.06) 0%, transparent 48%),
            radial-gradient(circle at 85% 85%, rgba(238, 44, 40, 0.04) 0%, transparent 45%);
        }
        .reveal {
          animation: reveal-in 800ms cubic-bezier(0.32, 0.72, 0, 1) forwards;
        }
        @keyframes reveal-in {
          from {
            opacity: 0;
            transform: translateY(16px);
            filter: blur(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
            filter: blur(0);
          }
        }
      `}</style>

      {/* Fixed Ambient Mesh */}
      <div className="mesh-container" aria-hidden="true" />

      <SEO
        title="Programmes | InnovaTech"
        description="Apply for hands-on SIWES, industrial training, and NYSC placement in Nigeria and Jigawa State. Gain real-world experience in software engineering, cybersecurity, and cloud systems at InnovaTech."
        keywords="best place to apply or do siwes, best place for siwes in nigeria, siwes placement in dutse jigawa, industrial training tech company nigeria, tech companies accepting it students in nigeria, nysc tech ppa jigawa, where to do siwes computer science, it placement northern nigeria, innovatech siwes"
        canonical="/programmes"
      >
        {/* EducationalOccupationalProgram schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOccupationalProgram",
            name: "InnovaTech SIWES & Industrial Training Placement",
            description:
              "Structured hands-on industrial training program for Nigerian university and polytechnic students in software engineering, cybersecurity, and IT infrastructure.",
            provider: {
              "@type": "Organization",
              name: "InnovaTech Consultancy Limited",
              url: "https://www.innovatech-ng.com",
              address: {
                "@type": "PostalAddress",
                streetAddress: "No.2 Salmaniyya Plaza, Opp. TCN, Ibrahim Aliyu Bypass",
                addressLocality: "Dutse",
                addressRegion: "Jigawa",
                addressCountry: "NG",
              },
            },
            timeToComplete: "P6M",
            occupationalCategory: "15-1252.00",
            educationalProgramMode: "Onsite",
            programPrerequisites:
              "Student in Computer Science, Cyber Security, Software Engineering, IT, or Electrical/Computer Engineering.",
          })}
        </script>

        {/* FAQPage Schema for SIWES & NYSC Search Intent */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Where is the best place to apply or do SIWES in Jigawa State and Nigeria?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "InnovaTech Consultancy Limited in Dutse, Jigawa State is the leading technology company for SIWES (Students Industrial Work Experience Scheme) and industrial training in Northern Nigeria. Students work on live production software, network deployments, and cybersecurity under senior engineer mentorship.",
                },
              },
              {
                "@type": "Question",
                name: "Which university and polytechnic students are eligible for SIWES at InnovaTech?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "InnovaTech accepts students from all accredited Nigerian institutions, including Federal University Dutse (FUD), Sule Lamido University (SLU) Kafin Hausa, Bayero University Kano (BUK), Kano University of Science and Technology (KUST) Wudil, and Jigawa State Polytechnic studying Computer Science, IT, Cyber Security, Software Engineering, or related technical disciplines.",
                },
              },
              {
                "@type": "Question",
                name: "What is the duration of SIWES industrial training at InnovaTech?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "InnovaTech provides structured 3-month and 6-month SIWES industrial training attachments, as well as 1-year NYSC primary assignment placements (PPA).",
                },
              },
              {
                "@type": "Question",
                name: "How do I apply for SIWES or NYSC placement at InnovaTech?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Students can apply directly online through the InnovaTech programmes portal by filling out the placement form and uploading their institutional SIWES letter of introduction and student ID card.",
                },
              },
            ],
          })}
        </script>
      </SEO>

      <div className="relative z-10 flex w-full max-w-[1024px] flex-col">
        {/* Header Block */}
        <div className="reveal mb-12 flex flex-col gap-6 border-b border-black/[0.06] dark:border-white/10 pb-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
              03 · PROGRAMMES
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] text-text-main dark:text-white leading-[0.98]">
            Structure &amp; <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-red-500">
              Placement.
            </span>
          </h1>
          <p className="max-w-2xl text-lg font-light leading-relaxed text-text-sub dark:text-gray-400">
            We offer hands-on training opportunities for students and corps members looking to gain real-world industry experience in secure software development and IT infrastructure design.
          </p>
        </div>

        {/* Tab Selection */}
        <div className="reveal flex justify-center mb-10">
          <div className="flex p-1 rounded-full bg-white/50 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-sm backdrop-blur-md">
            <button
              onClick={() => {
                setActiveTab("siwes");
                setIsFormOpen(false);
              }}
              className={`flex items-center gap-1.5 px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] cursor-pointer ${
                activeTab === "siwes"
                  ? "bg-white dark:bg-[#162A45] text-primary shadow-sm border border-black/[0.03] dark:border-white/5"
                  : "text-text-sub dark:text-gray-400 hover:text-text-main dark:hover:text-white"
              }`}
            >
              SIWES Placement
            </button>
            <button
              onClick={() => {
                setActiveTab("nysc");
                setIsFormOpen(false);
              }}
              className={`flex items-center gap-1.5 px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] cursor-pointer ${
                activeTab === "nysc"
                  ? "bg-white dark:bg-[#162A45] text-primary shadow-sm border border-black/[0.03] dark:border-white/5"
                  : "text-text-sub dark:text-gray-400 hover:text-text-main dark:hover:text-white"
              }`}
            >
              NYSC Programme
            </button>
          </div>
        </div>

        {/* Programme Content Card */}
        <div className="reveal p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft mb-8">
          <div className="rounded-[1.85rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 p-6 md:p-10 flex flex-col md:flex-row gap-8 items-center md:items-start relative overflow-hidden">
            
            {/* Background Accent Blob */}
            <div className="absolute inset-0 bg-[radial-gradient(400px_200px_at_50%_0%,rgba(238,44,40,0.06),transparent_70%)] pointer-events-none" />

            <div className="flex flex-col flex-grow">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-text-main dark:text-white mb-4">
                {activeTab === "siwes"
                  ? "SIWES Student Internship Placement"
                  : "NYSC Corps Member Placement"}
              </h2>
              <p className="text-sm md:text-base text-text-sub dark:text-gray-300 leading-relaxed mb-6 font-light max-w-3xl">
                {activeTab === "siwes"
                  ? "Start your professional career with our structured SIWES student internship. Perfect for university and polytechnic students who want practical experience in software development, computer networking, cybersecurity, or IT support. Work on real projects, get mentored by experienced engineers, and build skills that employers hire for."
                  : "Join InnovaTech for your NYSC primary assignment in Jigawa State. We offer a clear pathway to launch your tech career. Work on real client projects, help build modern software, assist with IT training, and develop practical job skills. We help you turn what you learned in school into real-world professional experience."}
              </p>

              <div className="flex flex-wrap gap-2 mb-8">
                {(activeTab === "siwes"
                  ? [
                      "3 to 6 Months Duration",
                      "Local Placement (Dutse, Jigawa)",
                      "Hands-on Practical Training",
                      "Office & Computer Lab Work",
                      "Daily Work Reports & Guidance",
                    ]
                  : [
                      "1 Year Service Period",
                      "Place of Primary Assignment (PPA)",
                      "Team Collaboration & Support",
                      "Real-world Project Experience",
                      "Post-Service Job Opportunities",
                    ]
                ).map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full bg-canvas dark:bg-[#0C1B2E] px-3 py-1 text-[10px] font-semibold text-text-sub dark:text-gray-300 border border-black/[0.03] dark:border-white/5 uppercase tracking-wider"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Apply Trigger Button */}
              {(() => {
                const isClosed = activeTab === "siwes"
                  ? programStatuses.siwes === "disabled"
                  : programStatuses.nysc === "disabled";
                
                if (isClosed) {
                  return (
                    <div className="p-5 rounded-2xl border border-red-200 bg-red-50/50 dark:bg-red-950/10 dark:border-red-900/30 text-red-600 dark:text-red-400 text-xs md:text-sm font-semibold max-w-xl leading-relaxed space-y-2 relative z-10">
                      <p className="flex items-center gap-2 font-bold text-sm">
                        <AlertTriangle className="w-[18px] h-[18px]" />
                        <span>Applications for this program are currently closed.</span>
                      </p>
                      <p className="text-text-sub dark:text-gray-400 text-xs font-normal">
                        Please contact us via email at <a href="mailto:inquiry@innovatech-ng.com" className="text-primary hover:underline font-bold">inquiry@innovatech-ng.com</a>, call us, or walk to our office for further inquiries.
                      </p>
                    </div>
                  );
                }

                return (
                  <button
                    onClick={handleApplyClick}
                    className="group w-full md:w-fit bg-primary hover:bg-red-600 text-white font-bold py-3.5 px-8 rounded-xl shadow-lg shadow-primary/20 active:scale-95 transition-all text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer relative z-10"
                  >
                    <span>{isFormOpen ? "Hide Application Form" : "Apply For Placement"}</span>
                    {isFormOpen ? (
                      <ChevronUp className="w-4 h-4 transform transition-transform group-hover:translate-y-0.5" />
                    ) : (
                      <ChevronDown className="w-4 h-4 transform transition-transform group-hover:translate-y-0.5" />
                    )}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Dynamic Accordion Dropdown Form */}
        <div
          className={`overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] ${
            isFormOpen ? "max-h-[3000px] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
          }`}
        >
          <div className="p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft">
            <form
              onSubmit={handleSubmit}
              className="rounded-[1.85rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 p-6 md:p-10 space-y-8 text-sm text-text-main dark:text-white"
            >
              {activeTab === "siwes" ? (
                <>
                  {/* SIWES FORM */}
                  {/* Section A: Personal Information */}
                  <div className="space-y-4">
                    <h3 className="font-mono text-xs font-bold tracking-widest text-primary uppercase border-b border-black/[0.05] dark:border-white/5 pb-2">
                      Section A: Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="fullName" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          required
                          autoComplete="name"
                          value={siwesForm.fullName}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="email" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          autoComplete="email"
                          value={siwesForm.email}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="phone" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          WhatsApp Phone Number *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          autoComplete="tel"
                          value={siwesForm.phone}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="currentLocation" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Current Location (City and State) *
                        </label>
                        <input
                          type="text"
                          id="currentLocation"
                          name="currentLocation"
                          required
                          value={siwesForm.currentLocation || ""}
                          onChange={handleInputChange}
                          placeholder="e.g., Dutse, Jigawa"
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section B: School Information */}
                  <div className="space-y-4">
                    <h3 className="font-mono text-xs font-bold tracking-widest text-primary uppercase border-b border-black/[0.05] dark:border-white/5 pb-2">
                      Section B: School Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="institutionName" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          School / Institution Name *
                        </label>
                        <input
                          type="text"
                          id="institutionName"
                          name="institutionName"
                          required
                          value={siwesForm.institutionName || ""}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="courseOfStudy" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Course of Study *
                        </label>
                        <input
                          type="text"
                          id="courseOfStudy"
                          name="courseOfStudy"
                          required
                          value={siwesForm.courseOfStudy || ""}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="levelYear" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Level or Year of Study *
                        </label>
                        <select
                          id="levelYear"
                          name="levelYear"
                          required
                          value={siwesForm.levelYear || "300L"}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        >
                          <option value="200L">200L</option>
                          <option value="300L">300L</option>
                          <option value="400L">400L</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="matricNumber" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Matriculation / Student Number *
                        </label>
                        <input
                          type="text"
                          id="matricNumber"
                          name="matricNumber"
                          required
                          value={siwesForm.matricNumber || ""}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section C: Placement Details & Skills */}
                  <div className="space-y-4">
                    <h3 className="font-mono text-xs font-bold tracking-widest text-primary uppercase border-b border-black/[0.05] dark:border-white/5 pb-2">
                      Section C: Placement Details &amp; Skills
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="proposedDuration" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Internship Duration *
                        </label>
                        <select
                          id="proposedDuration"
                          name="proposedDuration"
                          required
                          value={siwesForm.proposedDuration || "6 Months"}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        >
                          <option value="3 Months">3 Months</option>
                          <option value="6 Months">6 Months</option>
                          <option value="1 Year">1 Year</option>
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="startDate" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                            Expected Start Date *
                          </label>
                          <input
                            type="date"
                            id="startDate"
                            name="startDate"
                            required
                            value={siwesForm.startDate || ""}
                            onChange={handleInputChange}
                            className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-3 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="endDate" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                            Expected End Date *
                          </label>
                          <input
                            type="date"
                            id="endDate"
                            name="endDate"
                            required
                            value={siwesForm.endDate || ""}
                            onChange={handleInputChange}
                            className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-3 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="desiredDepartment" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Desired Team / Department *
                        </label>
                        <select
                          id="desiredDepartment"
                          name="desiredDepartment"
                          required
                          value={siwesForm.desiredDepartment || "Backend Development"}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        >
                          <option value="Backend Development">Backend Development</option>
                          <option value="Frontend Development">Frontend Development</option>
                          <option value="Cybersecurity">Cybersecurity</option>
                          <option value="Product Design">Product Design</option>
                          <option value="Solar Installation">Solar Installation</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="coreSkills" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Your Tech Skills (e.g. HTML, Python, Networking, Design)
                        </label>
                        <input
                          type="text"
                          id="coreSkills"
                          name="coreSkills"
                          value={siwesForm.coreSkills || ""}
                          onChange={handleInputChange}
                          placeholder="e.g., Python, React, Network Security"
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section D: File Uploads */}
                  <div className="space-y-4">
                    <h3 className="font-mono text-xs font-bold tracking-widest text-primary uppercase border-b border-black/[0.05] dark:border-white/5 pb-2">
                      Section D: File Uploads (under 4.0MB)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <label className="flex flex-col gap-2 p-5 border border-dashed border-border-subtle dark:border-white/10 rounded-2xl bg-canvas dark:bg-[#0C1B2E] items-center text-center cursor-pointer hover:border-primary/40 dark:hover:border-primary/40 hover:bg-black/[0.01] dark:hover:bg-white/[0.01] transition-all duration-300 w-full">
                        <Upload className="w-9 h-9 text-primary mb-2" />
                        <span className="text-xs font-bold text-text-main dark:text-white">SIWES / Placement Letter from School (PDF) *</span>
                        <input
                          type="file"
                          accept=".pdf"
                          required
                          onChange={(e) => handleFileChange(e, "siwesLetter")}
                          className="hidden"
                        />
                        <span className="text-xs text-text-sub dark:text-gray-400 mt-1">
                          {siwesLetter ? "File loaded" : "Click to select PDF"}
                        </span>
                        {siwesLetter && <span className="text-[10px] text-green-500 font-bold mt-1">Loaded successfully</span>}
                      </label>
                      <label className="flex flex-col gap-2 p-5 border border-dashed border-border-subtle dark:border-white/10 rounded-2xl bg-canvas dark:bg-[#0C1B2E] items-center text-center cursor-pointer hover:border-primary/40 dark:hover:border-primary/40 hover:bg-black/[0.01] dark:hover:bg-white/[0.01] transition-all duration-300 w-full">
                        <Award className="w-9 h-9 text-primary mb-2" />
                        <span className="text-xs font-bold text-text-main dark:text-white">Student ID Card (PDF or Image) *</span>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          required
                          onChange={(e) => handleFileChange(e, "siwesId")}
                          className="hidden"
                        />
                        <span className="text-xs text-text-sub dark:text-gray-400 mt-1">
                          {siwesIdCard ? "File loaded" : "Click to select file"}
                        </span>
                        {siwesIdCard && <span className="text-[10px] text-green-500 font-bold mt-1">Loaded successfully</span>}
                      </label>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* NYSC FORM */}
                  {/* Section A: Personal Information */}
                  <div className="space-y-4">
                    <h3 className="font-mono text-xs font-bold tracking-widest text-primary uppercase border-b border-black/[0.05] dark:border-white/5 pb-2">
                      Section A: Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="fullName" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          required
                          autoComplete="name"
                          value={nyscForm.fullName}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="email" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          autoComplete="email"
                          value={nyscForm.email}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="phone" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          WhatsApp Phone Number *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          autoComplete="tel"
                          value={nyscForm.phone}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="currentStateOfDeployment" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Current State of Deployment *
                        </label>
                        <input
                          type="text"
                          id="currentStateOfDeployment"
                          name="currentStateOfDeployment"
                          required
                          value={nyscForm.currentStateOfDeployment || ""}
                          onChange={handleInputChange}
                          placeholder="e.g., Jigawa State"
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section B: NYSC Specifics */}
                  <div className="space-y-4">
                    <h3 className="font-mono text-xs font-bold tracking-widest text-primary uppercase border-b border-black/[0.05] dark:border-white/5 pb-2">
                      Section B: NYSC Specifics
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="stateCode" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          NYSC State Code *
                        </label>
                        <input
                          type="text"
                          id="stateCode"
                          name="stateCode"
                          required
                          value={nyscForm.stateCode || ""}
                          onChange={handleInputChange}
                          placeholder="e.g., JG/25B/1234"
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="callUpNumber" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Call-Up Number *
                        </label>
                        <input
                          type="text"
                          id="callUpNumber"
                          name="callUpNumber"
                          required
                          value={nyscForm.callUpNumber || ""}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="batchStream" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Batch &amp; Stream *
                        </label>
                        <select
                          id="batchStream"
                          name="batchStream"
                          required
                          value={nyscForm.batchStream || "Batch A Stream 1"}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        >
                          <option value="Batch A Stream 1">Batch A Stream 1</option>
                          <option value="Batch A Stream 2">Batch A Stream 2</option>
                          <option value="Batch B Stream 1">Batch B Stream 1</option>
                          <option value="Batch B Stream 2">Batch B Stream 2</option>
                          <option value="Batch C Stream 1">Batch C Stream 1</option>
                          <option value="Batch C Stream 2">Batch C Stream 2</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="popMonthYear" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Passing Out Date (Month &amp; Year) *
                        </label>
                        <input
                          type="text"
                          id="popMonthYear"
                          name="popMonthYear"
                          required
                          placeholder="e.g., October 2026"
                          value={nyscForm.popMonthYear || ""}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section C: Academic & Professional Information */}
                  <div className="space-y-4">
                    <h3 className="font-mono text-xs font-bold tracking-widest text-primary uppercase border-b border-black/[0.05] dark:border-white/5 pb-2">
                      Section C: Academic &amp; Professional Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="higherInstitution" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          University / College Attended *
                        </label>
                        <input
                          type="text"
                          id="higherInstitution"
                          name="higherInstitution"
                          required
                          value={nyscForm.higherInstitution || ""}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="qualification" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Degree / Qualification Obtained *
                        </label>
                        <select
                          id="qualification"
                          name="qualification"
                          required
                          value={nyscForm.qualification || "BSc"}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        >
                          <option value="BSc">BSc</option>
                          <option value="BTech">BTech</option>
                          <option value="HND">HND</option>
                          <option value="MSc">MSc</option>
                          <option value="PhD">PhD</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="courseOfStudy" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Course of Study *
                        </label>
                        <input
                          type="text"
                          id="courseOfStudy"
                          name="courseOfStudy"
                          required
                          value={nyscForm.courseOfStudy || ""}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="roleAppliedFor" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Desired Role *
                        </label>
                        <select
                          id="roleAppliedFor"
                          name="roleAppliedFor"
                          required
                          value={nyscForm.roleAppliedFor || "Software Engineering"}
                          onChange={handleInputChange}
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        >
                          <option value="Software Engineering">Software Engineering</option>
                          <option value="Cybersecurity">Cybersecurity</option>
                          <option value="Network Infrastructure">Network Infrastructure</option>
                          <option value="Product Design">Product Design</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="coreSkills" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Skills &amp; Technologies You Know *
                        </label>
                        <input
                          type="text"
                          id="coreSkills"
                          name="coreSkills"
                          required
                          value={nyscForm.coreSkills || ""}
                          onChange={handleInputChange}
                          placeholder="e.g., React, Python, Cisco routing"
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="portfolioUrl" className="text-xs font-semibold text-text-sub dark:text-gray-300">
                          Portfolio Link (Optional)
                        </label>
                        <input
                          type="url"
                          id="portfolioUrl"
                          name="portfolioUrl"
                          autoComplete="url"
                          value={nyscForm.portfolioUrl || ""}
                          onChange={handleInputChange}
                          placeholder="https://github.com/username"
                          className="w-full bg-canvas dark:bg-[#0C1B2E] border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white outline-none focus:ring-2 focus:ring-primary/40 min-h-[48px]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section D: File Uploads */}
                  <div className="space-y-4">
                    <h3 className="font-mono text-xs font-bold tracking-widest text-primary uppercase border-b border-black/[0.05] dark:border-white/5 pb-2">
                      Section D: File Uploads (under 4.0MB)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <label className="flex flex-col gap-2 p-5 border border-dashed border-border-subtle dark:border-white/10 rounded-2xl bg-canvas dark:bg-[#0C1B2E] items-center text-center cursor-pointer hover:border-primary/40 dark:hover:border-primary/40 hover:bg-black/[0.01] dark:hover:bg-white/[0.01] transition-all duration-300 w-full">
                        <Upload className="w-9 h-9 text-primary mb-2" />
                        <span className="text-xs font-bold text-text-main dark:text-white">NYSC Call-up or Posting Letter (PDF) *</span>
                        <input
                          type="file"
                          accept=".pdf"
                          required
                          onChange={(e) => handleFileChange(e, "nyscLetter")}
                          className="hidden"
                        />
                        <span className="text-xs text-text-sub dark:text-gray-400 mt-1">
                          {nyscLetter ? "File loaded" : "Click to select PDF"}
                        </span>
                        {nyscLetter && <span className="text-[10px] text-green-500 font-bold mt-1">Loaded successfully</span>}
                      </label>
                      <label className="flex flex-col gap-2 p-5 border border-dashed border-border-subtle dark:border-white/10 rounded-2xl bg-canvas dark:bg-[#0C1B2E] items-center text-center cursor-pointer hover:border-primary/40 dark:hover:border-primary/40 hover:bg-black/[0.01] dark:hover:bg-white/[0.01] transition-all duration-300 w-full">
                        <FileText className="w-9 h-9 text-primary mb-2" />
                        <span className="text-xs font-bold text-text-main dark:text-white">Resume / CV (PDF) *</span>
                        <input
                          type="file"
                          accept=".pdf"
                          required
                          onChange={(e) => handleFileChange(e, "nyscResume")}
                          className="hidden"
                        />
                        <span className="text-xs text-text-sub dark:text-gray-400 mt-1">
                          {nyscResume ? "File loaded" : "Click to select PDF"}
                        </span>
                        {nyscResume && <span className="text-[10px] text-green-500 font-bold mt-1">Loaded successfully</span>}
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* Submit Button */}
              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-primary hover:bg-red-600 text-white font-bold py-3.5 px-10 rounded-xl shadow-lg shadow-primary/20 active:scale-[0.98] transition-all text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="size-4 rounded-full border-2 border-white/20 border-t-white animate-spin"></div>
                      <span>Submitting Application...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Submit Application</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Minimalist Overview Highlights & FAQ Section */}
        <section className="reveal mt-28 md:mt-36 flex flex-col gap-24">
          {/* Section Header */}
          <div className="flex flex-col gap-5 text-left border-t border-black/[0.06] dark:border-white/10 pt-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
                04 · ADVANTAGE
              </span>
            </div>
            
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.035em] text-text-main dark:text-white leading-[1.05]">
              Why We Lead in <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-500">
                Industrial Training.
              </span>
            </h2>
            <p className="text-sm sm:text-base text-text-sub dark:text-gray-400 font-light leading-relaxed max-w-2xl">
              We provide structured software engineering, cybersecurity, and cloud network deployments for undergraduate and polytechnic students across Northern Nigeria.
            </p>

            {/* Three Cards Matching Site's Nested Glass Card Language */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
              <div className="p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft">
                <div className="rounded-[1.85rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 p-7 flex flex-col justify-between h-full relative overflow-hidden">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-mono text-xs font-bold tracking-widest text-primary">01</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-text-sub dark:text-gray-400 bg-black/[0.03] dark:bg-white/5 px-2.5 py-1 rounded-full border border-black/[0.03] dark:border-white/5">
                      Production
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-text-main dark:text-white mb-2.5">
                      Live Projects
                    </h3>
                    <p className="text-xs sm:text-sm font-light text-text-sub dark:text-gray-400 leading-relaxed">
                      Write real code for production client applications, configure live network architectures, and build verifiable portfolio software.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft">
                <div className="rounded-[1.85rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 p-7 flex flex-col justify-between h-full relative overflow-hidden">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-mono text-xs font-bold tracking-widest text-primary">02</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-text-sub dark:text-gray-400 bg-black/[0.03] dark:border-white/5 px-2.5 py-1 rounded-full border border-black/[0.03] dark:border-white/5">
                      Guidance
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-text-main dark:text-white mb-2.5">
                      1-on-1 Mentorship
                    </h3>
                    <p className="text-xs sm:text-sm font-light text-text-sub dark:text-gray-400 leading-relaxed">
                      Receive daily code reviews and personalized technical guidance from senior engineers across backend, UI/UX, and cybersecurity.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft">
                <div className="rounded-[1.85rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 p-7 flex flex-col justify-between h-full relative overflow-hidden">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-mono text-xs font-bold tracking-widest text-primary">03</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-text-sub dark:text-gray-400 bg-black/[0.03] dark:border-white/5 px-2.5 py-1 rounded-full border border-black/[0.03] dark:border-white/5">
                      Career
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-text-main dark:text-white mb-2.5">
                      Logbook &amp; PPA
                    </h3>
                    <p className="text-xs sm:text-sm font-light text-text-sub dark:text-gray-400 leading-relaxed">
                      Rigorous weekly logbook sign-offs and automatic fast-track consideration for NYSC PPA and full-time engineering placement.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Minimalist FAQ Accordion Section */}
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
                  05 · QUESTIONS
                </span>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-[-0.03em] text-text-main dark:text-white leading-[1.1]">
                Frequently Asked <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-500">
                  Questions.
                </span>
              </h3>
            </div>

            <div className="flex flex-col gap-4">
              {[
                {
                  q: "Why is InnovaTech the best place to apply or do SIWES in Nigeria and Jigawa State?",
                  a: "InnovaTech is Northern Nigeria's premier technology company offering fully immersive, hands-on SIWES placement. Unlike generic computer centers, students at InnovaTech work on live production web applications, cybersecurity audits, and networking installations under 1-on-1 mentorship from senior software engineers."
                },
                {
                  q: "Which schools and university courses qualify for SIWES placement at InnovaTech?",
                  a: "We accept undergraduate and diploma students from Federal University Dutse (FUD), Sule Lamido University (SLU) Kafin Hausa, Bayero University Kano (BUK), KUST Wudil, Jigawa State Polytechnic, and all accredited Nigerian tertiary institutions. Ideal courses include Computer Science, Software Engineering, Cyber Security, Information Technology, Computer Engineering, and Electrical Engineering."
                },
                {
                  q: "What is the duration of the Industrial Training (IT) attachment?",
                  a: "We accommodate 3-month, 6-month, and 12-month SIWES/IT internships, as well as 1-year NYSC primary assignment placements (PPA)."
                },
                {
                  q: "Where is the InnovaTech training center located for daily physical attendance?",
                  a: "InnovaTech is located at No.2 Salmaniyya Plaza, Opposite TCN, Ibrahim Aliyu Bypass, Dutse, Jigawa State, Nigeria. Our physical facility is fully equipped with high-speed fiber internet and dedicated development workstations."
                },
                {
                  q: "Can NYSC corps members serve their PPA at InnovaTech in Jigawa State?",
                  a: "Yes! We welcome tech-focused corps members deployed to Jigawa State to serve their primary assignment with us, with opportunities for post-service full-time retention."
                }
              ].map((faq, idx) => (
                <div
                  key={idx}
                  className="p-1 rounded-[1.75rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft transition-all duration-300"
                >
                  <div className="rounded-[1.5rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full p-6 sm:p-7 text-left flex items-center justify-between gap-4 font-semibold text-sm sm:text-base text-text-main dark:text-white cursor-pointer hover:bg-black/[0.01] dark:hover:bg-white/[0.01] transition-colors"
                    >
                      <span className="font-display tracking-tight">{faq.q}</span>
                      <span className="font-mono text-xs font-bold text-primary shrink-0 ml-2">
                        {openFaq === idx ? "−" : "+"}
                      </span>
                    </button>
                    {openFaq === idx && (
                      <div className="px-6 pb-6 sm:px-7 sm:pb-7 pt-1 text-xs sm:text-sm text-text-sub dark:text-gray-300 font-light leading-relaxed border-t border-black/[0.04] dark:border-white/5">
                        {faq.a}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>

    {/* Custom Success/Error Popup Modal */}
    {popup && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
        <div className="w-full max-w-sm bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/10 rounded-2xl p-6 shadow-2xl space-y-6 animate-in zoom-in-95 duration-200">
          <div className="flex flex-col items-center text-center gap-4">
            <div className={`size-16 rounded-full flex items-center justify-center ${
              popup.type === "success" ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" : "bg-red-500/10 text-red-500 border border-red-500/20"
            }`}>
              {popup.type === "success" ? (
                <CheckCircle className="w-9 h-9" />
              ) : (
                <AlertCircle className="w-9 h-9" />
              )}
            </div>
            <div className="space-y-1.5">
              <h4 className="font-display font-bold text-lg text-text-main dark:text-white capitalize">
                {popup.type === "success" ? "Success" : "Error"}
              </h4>
              <p className="text-xs text-text-sub dark:text-gray-300 leading-relaxed">
                {popup.message}
              </p>
            </div>
          </div>

          <div className="flex justify-center uppercase font-bold tracking-wider text-xs">
            <button
              type="button"
              onClick={() => setPopup(null)}
              className={`px-8 py-2.5 rounded-xl text-white shadow-md active:scale-95 transition-all cursor-pointer ${
                popup.type === "success" ? "bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/20" : "bg-red-500 hover:bg-red-600 shadow-red-500/20"
              }`}
            >
              Okay
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ProgrammesScreen;
